import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/lib/utils.js";

/**
 * @typedef {'default'|'primary'|'secondary'|'danger'|'outline'} ChipVariant
 */

/**
 * @typedef {'sm'|'md'|'lg'} ChipSize
 */

const chipButtonFocusClasses =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background";

const chipVariants = cva(
  "inline-flex items-center rounded-full border transition-colors data-[selected=true]:ring-2 data-[selected=true]:ring-ring data-[selected=true]:ring-offset-2 data-[selected=true]:ring-offset-background [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border-border bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
        primary:
          "border border-black bg-muted text-foreground hover:bg-accent hover:text-accent-foreground dark:border-slate-200 "
          + "data-[selected=true]:border-black data-[selected=true]:bg-slate-600 data-[selected=true]:text-white "
          + "data-[selected=true]:hover:bg-slate-600/90 dark:data-[selected=true]:border-slate-200 dark:data-[selected=true]:bg-slate-500 "
          + "data-[selected=true]:ring-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        danger:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
      },
      size: {
        sm: "gap-1 min-h-6 text-xs font-semibold px-2 py-0.5 [&_svg]:size-3",
        md: "gap-1.5 min-h-8 text-sm font-medium px-2.5 py-1 [&_svg]:size-4",
        lg: "gap-2 min-h-9 text-base font-medium px-3 py-1.5 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

const chipSegmentClasses = {
  sm: {
    main: "gap-1 px-2 py-0.5 text-xs font-semibold [&_svg]:size-3",
    remove: "px-1.5 py-0.5 [&_svg]:size-3",
  },
  md: {
    main: "gap-1.5 px-2.5 py-1 text-sm font-medium [&_svg]:size-4",
    remove: "px-2 py-1 [&_svg]:size-4",
  },
  lg: {
    main: "gap-2 px-3 py-1.5 text-base font-medium [&_svg]:size-4",
    remove: "px-2.5 py-1.5 [&_svg]:size-4",
  },
};

const ChipGroupContext = React.createContext(null);

/**
 * @returns {{
 *   type: 'single'|'multiple',
 *   value: string|string[]|undefined,
 *   selectValue: (chipValue: string) => void,
 *   isSelected: (chipValue: string) => boolean
 * } | null}
 */
function useChipGroupContext() {
  return React.useContext(ChipGroupContext);
}

function collectMainButtons(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll("[data-chip-main='true']")
  ).filter((el) => el instanceof HTMLElement);
}

/**
 * Chip group for single or multiple selection with listbox semantics.
 * Keyboard: Tab visits every focusable (main + remove). Arrow keys move focus between main option buttons only.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {'single'|'multiple'} [props.type]
 * @param {string|string[]|undefined} [props.value] - Controlled value
 * @param {string|string[]|undefined} [props.defaultValue]
 * @param {(next: string|string[]) => void} [props.onValueChange]
 * @param {string} [props.className]
 * @param {string} [props['aria-label']]
 * @param {string} [props['aria-labelledby']]
 */
function ChipGroup({
  children,
  type = "single",
  value: valueProp,
  defaultValue,
  onValueChange,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}) {
  const listRef = React.useRef(null);
  const isControlled = valueProp !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = React.useState(
    defaultValue ?? (type === "multiple" ? [] : undefined)
  );

  const value = isControlled ? valueProp : uncontrolledValue;

  const setValue = React.useCallback(
    (next) => {
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      onValueChange?.(next);
    },
    [isControlled, onValueChange]
  );

  const isSelected = React.useCallback(
    (chipValue) => {
      if (type === "single") {
        return value === chipValue;
      }
      return Array.isArray(value) && value.includes(chipValue);
    },
    [type, value]
  );

  const selectValue = React.useCallback(
    (chipValue) => {
      if (type === "single") {
        if (value === chipValue) return;
        setValue(chipValue);
        return;
      }
      const arr = Array.isArray(value) ? [...value] : [];
      const i = arr.indexOf(chipValue);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(chipValue);
      setValue(arr);
    },
    [type, value, setValue]
  );

  const contextValue = React.useMemo(
    () => ({
      type,
      value,
      selectValue,
      isSelected,
    }),
    [type, value, selectValue, isSelected]
  );

  const handleKeyDown = React.useCallback((event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches("[data-chip-main='true']")) return;

    const mains = collectMainButtons(listRef.current);
    const idx = mains.indexOf(target);
    if (idx < 0) return;

    let nextIdx = idx;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIdx = Math.min(mains.length - 1, idx + 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIdx = Math.max(0, idx - 1);
    } else {
      return;
    }

    if (nextIdx !== idx) {
      event.preventDefault();
      mains[nextIdx]?.focus();
    }
  }, []);

  return (
    <ChipGroupContext.Provider value={contextValue}>
      <div
        ref={listRef}
        role='listbox'
        aria-multiselectable={type === "multiple"}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onKeyDown={handleKeyDown}
        className={cn("flex flex-wrap gap-2", className)}>
        {children}
      </div>
    </ChipGroupContext.Provider>
  );
}

/**
 * @param {React.ReactNode} children
 * @returns {string|undefined}
 */
function textFromChildren(children) {
  if (children == null || children === false) return undefined;
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    const parts = children.map((c) => textFromChildren(c)).filter(Boolean);
    return parts.length ? parts.join(" ") : undefined;
  }
  if (React.isValidElement(children)) {
    return textFromChildren(children.props.children);
  }
  return undefined;
}

/**
 * Pill chip / tag: static label, button, removable split control, or composition via Slot.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 * @param {string} [props.className]
 * @param {'default'|'primary'|'secondary'|'danger'|'outline'} [props.variant]
 * @param {'sm'|'md'|'lg'} [props.size]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.selected] - When defined, drives styles and aria (standalone: aria-pressed; in ChipGroup with value: aria-selected on option)
 * @param {(e: React.MouseEvent) => void} [props.onClick]
 * @param {(e: React.MouseEvent) => void} [props.onRemove]
 * @param {React.ReactNode} [props.icon]
 * @param {React.ReactNode} [props.endIcon]
 * @param {boolean} [props.asChild] - Single child only (Radix Slot); compose icons inside that child; invalid with onRemove (warns in dev)
 * @param {string} [props.value] - Chip identity when used inside ChipGroup
 * @param {React.Ref<HTMLElement>} ref - Forwarded to the primary focusable element (main button or Slot child)
 */
const Chip = React.forwardRef(
  (
    {
      children,
      className,
      variant,
      size,
      disabled = false,
      selected: selectedProp,
      onClick,
      onRemove,
      icon,
      endIcon,
      asChild = false,
      value: chipValue,
      ...rest
    },
    ref
  ) => {
    const group = useChipGroupContext();
    const inGroup = group != null;
    const hasGroupValue = chipValue !== undefined && chipValue !== null;

    const groupSelected =
      inGroup && hasGroupValue
        ? group.isSelected(String(chipValue))
        : undefined;
    const selected = inGroup && hasGroupValue ? groupSelected : selectedProp;

    const hasRemove = typeof onRemove === "function";

    const handleMainClick = React.useCallback(
      (e) => {
        if (inGroup && hasGroupValue) {
          group.selectValue(String(chipValue));
        }
        onClick?.(e);
      },
      [inGroup, hasGroupValue, group, chipValue, onClick]
    );

    const handleRemoveClick = React.useCallback(
      (e) => {
        e.stopPropagation();
        onRemove?.(e);
      },
      [onRemove]
    );

    const labelText = textFromChildren(children);
    const removeLabel =
      labelText != null && labelText.length > 0
        ? `Remove ${labelText}`
        : "Remove";

    const dataSelected = selected === true ? { "data-selected": "true" } : {};

    const listboxOptionProps =
      inGroup && hasGroupValue
        ? {
            role: "option",
            "aria-selected": Boolean(selected),
            "data-chip-main": "true",
          }
        : {};

    const togglePressedProps =
      !inGroup || !hasGroupValue
        ? typeof selected === "boolean"
          ? { "aria-pressed": selected }
          : {}
        : {};

    const shellClass = cn(chipVariants({ variant, size }), className);
    const seg = chipSegmentClasses[size ?? "md"];

    const content = (
      <>
        {icon ? <span className='inline-flex shrink-0'>{icon}</span> : null}
        {children != null ? (
          <span className='min-w-0 truncate'>{children}</span>
        ) : null}
        {endIcon ? (
          <span className='inline-flex shrink-0'>{endIcon}</span>
        ) : null}
      </>
    );

    // Static: no onClick, not in selectable group mode for this chip, no onRemove
    const isStatic =
      typeof onClick !== "function"
      && typeof onRemove !== "function"
      && !(inGroup && hasGroupValue);

    if (isStatic) {
      return (
        <span className={shellClass} {...dataSelected} {...rest}>
          {content}
        </span>
      );
    }

    if (hasRemove) {
      return (
        <span
          className={cn(shellClass, "p-0 gap-0")}
          {...dataSelected}
          {...rest}>
          <button
            type='button'
            disabled={disabled}
            className={cn(
              chipButtonFocusClasses,
              "disabled:pointer-events-none disabled:opacity-50",
              "inline-flex h-full min-h-0 flex-1 items-center justify-start rounded-none border-0 bg-transparent text-inherit shadow-none outline-none",
              "hover:bg-black/[0.06] dark:hover:bg-white/10",
              seg.main
            )}
            ref={ref}
            onClick={handleMainClick}
            {...listboxOptionProps}
            {...togglePressedProps}>
            {content}
          </button>
          <button
            type='button'
            disabled={disabled}
            aria-label={removeLabel}
            className={cn(
              chipButtonFocusClasses,
              "disabled:pointer-events-none disabled:opacity-50",
              "inline-flex shrink-0 items-center justify-center rounded-none border-0 border-l border-border/60 bg-transparent text-inherit shadow-none outline-none",
              "hover:bg-black/[0.06] dark:hover:bg-white/10",
              "rounded-r-full",
              seg.remove
            )}
            onClick={handleRemoveClick}>
            <X
              className={cn(
                "shrink-0 opacity-70",
                size === "sm" ? "size-3" : "size-4"
              )}
              aria-hidden
            />
          </button>
        </span>
      );
    }

    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        type={asChild ? undefined : "button"}
        className={cn(
          shellClass,
          chipButtonFocusClasses,
          "disabled:pointer-events-none disabled:opacity-50"
        )}
        ref={ref}
        disabled={asChild ? undefined : disabled}
        onClick={handleMainClick}
        {...dataSelected}
        {...listboxOptionProps}
        {...togglePressedProps}
        {...rest}>
        {asChild ? children : content}
      </Comp>
    );
  }
);
Chip.displayName = "Chip";

export { Chip, chipVariants, ChipGroup, useChipGroupContext };

// --- Example usage ---
// import { Link } from "react-router-dom";
// import { Chip, ChipGroup } from "@/components/ui/chip.jsx";
//
// <Chip>Static</Chip>
// <Chip variant="primary" onClick={() => {}}>Click me</Chip>
// <Chip selected={true} onClick={() => {}}>Selectable</Chip>
// <Chip onRemove={() => {}}>Removable</Chip>
// <Chip asChild variant="outline">
//   <Link to="/fleet">As link</Link>
// </Chip>
//
// <ChipGroup type="single" defaultValue="a" aria-label="Filters">
//   <Chip value="a">A</Chip>
//   <Chip value="b">B</Chip>
// </ChipGroup>
//
// <ChipGroup type="multiple" defaultValue={["x"]} aria-label="Tags">
//   <Chip value="x" onRemove={() => {}}>X</Chip>
//   <Chip value="y">Y</Chip>
// </ChipGroup>
